import { useState } from 'react';
import { useRouteLoaderData } from '@remix-run/react';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

const ROLLING_HEADER_HEIGHT = '2.5rem';
const ROTATION_MS = 5000;

// One cycle per message: fade in, hold, fade out. The text is swapped on
// `animationiteration`, i.e. while it is invisible, so no timer is needed and
// pausing the animation pauses the rotation with it.
const fadeCycle = keyframes`
  0%, 100% { opacity: 0; }
  8%, 92% { opacity: 1; }
`;

/**
 * BPES-254. Messages come from `ROLLING_HEADER_MESSAGES` via root loader data. One
 * message stays put; several rotate. Hover or focus pauses it (WCAG 2.2.2). Messages
 * are HTML, so inline `<b>` or `<span style="color:#F93822">` works.
 */
const RollingHeader = () => {
  const messages =
    (useRouteLoaderData('root') as { rollingHeaderMessages?: string[] })
      ?.rollingHeaderMessages ?? [];
  const [index, setIndex] = useState(0);

  if (!messages.length) return null;

  return (
    <Bar>
      <OffsetVar />
      <Message
        $rotates={messages.length > 1}
        onAnimationIteration={() => setIndex((current) => current + 1)}
        // Trusted HTML (`<b>`, `<span style="color:…">`): it comes only from env, which
        // only a deployer can set. Never feed this from request or CMS data.
        // Modulo here only, so a revalidated, shorter list stays in range.
        dangerouslySetInnerHTML={{ __html: messages[index % messages.length] }}
      />
    </Bar>
  );
};

// On `:root`, not `Root`: the mobile menu is portaled into <body>, outside `Root`.
// `--rolling-header-offset` (mobile): `Root` padding, header row and mobile menu.
// `--rolling-header-countdown-offset`: drops the desktop `PageCountdown`, which is
// fixed in the same centred spot, below the message. No breakpoint needed — the
// countdown is `position: static` at <= lg, where `top` does nothing.
const OffsetVar = createGlobalStyle`
  :root {
    --rolling-header-countdown-offset: 2rem;
  }

  @media ${({ theme }) => theme.breakpoints.lg} {
    :root {
      --rolling-header-offset: ${ROLLING_HEADER_HEIGHT};
    }
  }
`;

const Message = styled.p<{ $rotates: boolean }>`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $rotates }) =>
    $rotates &&
    css`
      animation: ${fadeCycle} ${ROTATION_MS}ms ease-in-out infinite;
    `}
`;

// Desktop: page-centred in the header row (1.5rem top padding, 1.5rem items).
// Mobile: a solid strip above the header, matching the page background.
// ponytail: /sizing-reference keeps the centred-logo header up to 1380px, so there
// the message overlaps the logo between 1280 and 1380px; key on useIsMobileNavigation
// if that page ever matters.
const Bar = styled.div`
  position: fixed;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  max-width: 40vw;
  height: 1.5rem;
  display: flex;
  align-items: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1rem;
  letter-spacing: 0.0094rem;

  &:focus-within ${Message} {
    animation-play-state: paused;
  }

  /* Hover only where it is real: a tap leaves :hover stuck on touch screens. */
  @media (hover: hover) {
    &:hover ${Message} {
      animation-play-state: paused;
    }
  }

  @media ${({ theme }) => theme.breakpoints.lg} {
    top: 0;
    left: 0;
    transform: none;
    width: 100%;
    max-width: none;
    height: ${ROLLING_HEADER_HEIGHT};
    justify-content: center;
    padding: 0 1rem;
    background-color: ${({ theme }) => theme.colors.colorMain};
    z-index: ${({ theme }) => theme.zIndex.header};
    font-size: 0.875rem;
  }
`;

export default RollingHeader;
